import { ImageProps } from "next/legacy/image";
import Image from "next/legacy/image";
import { KeyboardEvent, ReactNode } from "react";

export interface Props extends ImageProps {
  caption?: ReactNode | undefined;
  overlayItem?: ReactNode | undefined;
  // When provided, the image becomes clickable (e.g. to expand it to full
  // resolution). The handler is attached to the image element only, so it does
  // not swallow clicks on any overlayItem controls rendered on top of it.
  onImageClick?: (() => void) | undefined;
}

function ImageWithCaption({
  caption,
  overlayItem,
  onImageClick,
  className,
  ...props
}: Props) {
  // When the image is clickable, expose it as a keyboard-operable button so
  // keyboard-only users can focus and activate the full-resolution view.
  const interactiveProps = onImageClick
    ? {
        role: "button",
        tabIndex: 0,
        "aria-label": "View full-resolution image",
        onClick: onImageClick,
        onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onImageClick();
          }
        },
      }
    : {};

  return (
    <div className="flex flex-col">
      <div className="flex-init relative">
        <Image
          {...props}
          {...interactiveProps}
          alt=""
          className={`${className ?? ""}${onImageClick ? " cursor-pointer" : ""}`}
        />
        {overlayItem && (
          <div className="absolute top-2 right-2">{overlayItem}</div>
        )}
      </div>
      {caption && (
        <div className="flex-1 text-slate-500 text-2xl text-center italic">
          {caption}
        </div>
      )}
    </div>
  );
}

export default ImageWithCaption;
